import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin } from "lucide-react";
import { contactInfo } from "@/data/machines";
import { brand } from "@/data/brand";
import Reveal from "@/components/site/Reveal";

const socials = [
  { icon: Facebook, label: "Facebook", testId: "social-facebook" },
  { icon: Instagram, label: "Instagram", testId: "social-instagram" },
  { icon: Linkedin, label: "LinkedIn", testId: "social-linkedin" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-slate-950 text-slate-300 overflow-hidden">
      <div aria-hidden className="absolute -top-40 -left-24 w-[26rem] h-[26rem] bg-sky-500/10 blur-3xl rounded-full pointer-events-none" />
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-500/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <Reveal>
            <div className="flex items-center gap-3">
              <img
                src={brand.logoUrl}
                alt={`${contactInfo.companyName} logo`}
                className="h-12 w-12 rounded-lg object-cover ring-1 ring-white/10 shadow-lg shadow-black/40"
              />
              <span className="font-heading font-bold text-white text-lg tracking-tight">
                {contactInfo.companyName}
              </span>
            </div>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed">
              Venta y alquiler de maquinaria para construcción, jardinería e industria. Equipos
              revisados y entrega rápida en Toledo y alrededores.
            </p>
            <div className="mt-6 flex gap-3">
              {socials.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href="#"
                    aria-label={s.label}
                    data-testid={s.testId}
                    className="w-9 h-9 rounded-lg border border-slate-800 hover:border-sky-500 hover:text-sky-400 flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:shadow-sky-500/20"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider">
              Navegación
            </h4>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                ["#inicio", "Inicio"],
                ["#catalogo", "Maquinaria"],
                ["#tarifas", "Tarifas"],
                ["#contacto", "Contacto"],
              ].map(([href, label]) => (
                <li key={href}>
                  <a
                    href={href}
                    className="inline-flex items-center gap-2 hover:text-sky-400 transition-colors duration-300 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-slate-700 group-hover:bg-sky-400 transition-colors duration-300" />
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.16}>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider">
              Contacto
            </h4>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-sky-400 shrink-0" />
                <a href={`tel:${contactInfo.phoneRaw}`} className="hover:text-sky-400 transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-sky-400 shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="hover:text-sky-400 transition-colors break-all">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-sky-400 shrink-0" />
                <span>{contactInfo.address}</span>
              </li>
            </ul>
          </Reveal>

          <Reveal delay={0.24}>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider">
              Horario
            </h4>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-0.5 text-sky-400 shrink-0" />
                <span>{contactInfo.schedule}</span>
              </li>
            </ul>
            <a
              href={`tel:${contactInfo.phoneRaw}`}
              data-testid="footer-call-btn"
              className="shine group mt-7 inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 text-white font-semibold rounded-xl px-5 py-3 text-sm transition-all duration-300 w-full shadow-md shadow-sky-500/20 hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4 group-hover:wiggle" /> Llamar ahora
            </a>
          </Reveal>
        </div>

        <div className="mt-16 pt-6 border-t border-slate-800/70 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} {contactInfo.companyName}. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-5 text-xs text-slate-500">
            <a href="#" className="hover:text-slate-300 transition-colors">Aviso legal</a>
            <a href="#" className="hover:text-slate-300 transition-colors">Privacidad</a>
            <a href="#" className="hover:text-slate-300 transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
